import { createStore, useStore } from "zustand";
import { querystring } from "zustand-querystring";
import { useLocation } from "react-router";
import { AbvType, DietaryType } from "../discovery/v2/DiscoveryV2SearchStore";
import { customJsonFormat } from "./queryStringFormat";

const selectedContainerTypesList = [
  "cask",
  "can",
  "keg",
  "bottle",
  "bagInBox",
  "bag",
  "pouch",
  "jerryCan",
  "box",
] as const;

export type selectedTagsType = (typeof selectedContainerTypesList)[number];

export const isSelectedTag = (tag: string): tag is selectedTagsType => {
  return (selectedContainerTypesList as readonly string[]).includes(tag);
};

export type StorefrontFilterStoreState = {
  searchValue: string;
  selectedGroupIds: number[];
  selectedTags: selectedTagsType[];
  selectedPriceTier: number;
  selectedAvailabilityTier: number;
  selectedAbvTypes: AbvType[];
  selectedDietaryTypes: DietaryType[];
  filtersAppliedFromMarket: boolean;
  showOutOfStockProducts: boolean;
  hasInitializedOutOfStockProducts: boolean;
};

type StorefrontFilterStoreActions = {
  setState: (_state: StorefrontFilterStoreState) => void;
  setSearchValue: (_searchValue: string) => void;
  setSelectedGroupIds: (_selectedGroupIds: number[]) => void;
  setSelectedTags: (_selectedTags: selectedTagsType[]) => void;
  setSelectedPriceTier: (_selectedPriceTier: number) => void;
  setSelectedAvailabilityTier: (_selectedAvailabilityTier: number) => void;
  setAbvType: (_abvType: AbvType[]) => void;
  setDietaryType: (_dietaryType: DietaryType[]) => void;
  setFiltersAppliedFromMarket: (_filtersAppliedFromMarket: boolean) => void;
  setShowOutOfStockProducts: (_showOutOfStockProducts: boolean) => void;
  initializeShowOutOfStockProducts: (_showOutOfStockProducts: boolean) => void;
  reset: () => void;
};

const initialStorefrontFilterStoreState: StorefrontFilterStoreState = {
  searchValue: "",
  selectedGroupIds: [],
  selectedAbvTypes: [],
  selectedTags: [],
  selectedDietaryTypes: [],
  selectedPriceTier: 0,
  selectedAvailabilityTier: 0,
  filtersAppliedFromMarket: false,
  showOutOfStockProducts: false,
  hasInitializedOutOfStockProducts: false,
};

// Extract supplier name from current URL using React Router
const useSupplierFromUrl = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const suppliersIndex = pathSegments.indexOf("suppliers");
  if (suppliersIndex !== -1 && suppliersIndex + 1 < pathSegments.length) {
    return pathSegments[suppliersIndex + 1];
  }
  return null;
};

export const buildDynamicKeyForStorefrontFilterStore = (
  supplierUrlId: string | null,
) => {
  return supplierUrlId ? `storefrontState-${supplierUrlId}` : "storefrontState";
};

type StorefrontFilterStoreType = StorefrontFilterStoreState &
  StorefrontFilterStoreActions;

const createStorefrontFilterStore = (supplierName?: string | null) => {
  const effectiveSupplierName = supplierName;
  const dynamicKey = buildDynamicKeyForStorefrontFilterStore(
    effectiveSupplierName ?? null,
  );

  return createStore<StorefrontFilterStoreType>()(
    querystring<StorefrontFilterStoreType>(
      (set) => ({
        ...initialStorefrontFilterStoreState,
        setState: (newState: StorefrontFilterStoreState) => set(newState),
        setSearchValue: (searchValue: string) => set({ searchValue }),
        setSelectedGroupIds: (selectedGroupIds: number[]) =>
          set({ selectedGroupIds }),
        setSelectedTags: (selectedTags: selectedTagsType[]) =>
          set({ selectedTags }),
        setSelectedPriceTier: (selectedPriceTier: number) =>
          set({ selectedPriceTier }),
        setSelectedAvailabilityTier: (selectedAvailabilityTier: number) =>
          set({ selectedAvailabilityTier }),
        setAbvType: (abvType: AbvType[]) => set({ selectedAbvTypes: abvType }),
        setDietaryType: (dietaryType: DietaryType[]) =>
          set({ selectedDietaryTypes: dietaryType }),
        setFiltersAppliedFromMarket: (filtersAppliedFromMarket: boolean) =>
          set({ filtersAppliedFromMarket }),
        setShowOutOfStockProducts: (showOutOfStockProducts: boolean) =>
          set({
            showOutOfStockProducts,
            hasInitializedOutOfStockProducts: true,
          }),
        initializeShowOutOfStockProducts: (showOutOfStockProducts: boolean) =>
          set((state) =>
            state.hasInitializedOutOfStockProducts
              ? state
              : {
                  showOutOfStockProducts,
                  hasInitializedOutOfStockProducts: true,
                },
          ),
        reset: () => set(initialStorefrontFilterStoreState),
      }),
      {
        select(pathname) {
          // Only persist params if we're on the same specific supplier page
          const currentSupplier = pathname
            .split("/")
            .find((segment, index, array) => array[index - 1] === "suppliers");
          const persistParam =
            !!effectiveSupplierName &&
            currentSupplier === effectiveSupplierName;

          return {
            searchValue: persistParam,
            selectedGroupIds: persistParam,
            selectedTags: persistParam,
            selectedAbvTypes: persistParam,
          };
        },
        key: dynamicKey,
        format: customJsonFormat,
      },
    ),
  );
};

const storeInstances = new Map<
  string,
  ReturnType<typeof createStorefrontFilterStore>
>();

export function useStorefrontFilterStore(): StorefrontFilterStoreType;
export function useStorefrontFilterStore<T>(
  _selector: (_state: StorefrontFilterStoreType) => T,
  _supplierUrlId?: string,
): T;
export function useStorefrontFilterStore<T>(
  selector?: (_state: StorefrontFilterStoreType) => T,
  supplierUrlId?: string,
): T | StorefrontFilterStoreType {
  const supplierFromUrl = useSupplierFromUrl();
  const currentSupplier = supplierUrlId || supplierFromUrl;
  const key = currentSupplier ? `supplier-${currentSupplier}` : "default";

  if (!storeInstances.has(key)) {
    storeInstances.set(key, createStorefrontFilterStore(currentSupplier));
  }
  const store = storeInstances.get(key)!;

  const defaultSelector = (s: StorefrontFilterStoreType) => s as unknown as T;
  return useStore(store, selector || defaultSelector);
}
